<script setup>
import { computed } from 'vue';
import Draggable from 'vuedraggable';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import KanbanCard from './KanbanCard.vue';
import { conversationMatchesQuery } from '../constants';

const props = defineProps({
  column: {
    type: Object,
    required: true,
  },
  conversations: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  hasMore: {
    type: Boolean,
    default: false,
  },
  searchQuery: {
    type: String,
    default: '',
  },
  isFirst: {
    type: Boolean,
    default: false,
  },
  isLast: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'update:conversations',
  'change',
  'load-more',
  'edit',
  'delete',
  'move',
  'card-open',
  'card-menu',
]);

// A coluna virtual "sem coluna" nao pode ser editada nem removida.
const isVirtual = computed(() => props.column.id === 'none');

const visibleCount = computed(
  () =>
    props.conversations.filter(conversation =>
      conversationMatchesQuery(conversation, props.searchQuery)
    ).length
);

const isEmpty = computed(() => !props.loading && visibleCount.value === 0);

const isHidden = conversation =>
  !conversationMatchesQuery(conversation, props.searchQuery);
</script>

<template>
  <section
    class="flex h-full w-80 min-w-80 shrink-0 flex-col overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1"
  >
    <div class="h-1 w-full" :style="{ backgroundColor: column.color }" />
    <header class="flex items-center gap-2 px-3 py-2.5">
      <span
        class="size-2.5 shrink-0 rounded-full"
        :style="{ backgroundColor: column.color }"
      />
      <h2 class="truncate text-sm font-medium text-n-slate-12">
        {{ isVirtual ? $t('KANBAN.UNASSIGNED_COLUMN') : column.name }}
      </h2>
      <span
        class="rounded-md bg-n-alpha-2 px-1.5 text-xs font-medium tabular-nums text-n-slate-11"
      >
        {{ visibleCount }}
      </span>
      <div v-if="!isVirtual" class="ms-auto flex items-center gap-0.5">
        <button
          v-if="!isFirst"
          v-tooltip.top="$t('KANBAN.MOVE_LEFT')"
          type="button"
          class="rounded p-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
          @click="emit('move', -1)"
        >
          <Icon icon="i-lucide-chevron-left" class="size-3.5" />
        </button>
        <button
          v-if="!isLast"
          v-tooltip.top="$t('KANBAN.MOVE_RIGHT')"
          type="button"
          class="rounded p-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
          @click="emit('move', 1)"
        >
          <Icon icon="i-lucide-chevron-right" class="size-3.5" />
        </button>
        <button
          v-tooltip.top="$t('KANBAN.EDIT_COLUMN')"
          type="button"
          class="rounded p-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
          @click="emit('edit')"
        >
          <Icon icon="i-lucide-pencil" class="size-3.5" />
        </button>
        <button
          v-tooltip.top="$t('KANBAN.DELETE_COLUMN')"
          type="button"
          class="rounded p-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-ruby-11"
          @click="emit('delete')"
        >
          <Icon icon="i-lucide-trash-2" class="size-3.5" />
        </button>
      </div>
    </header>
    <div class="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-2">
      <div
        v-if="loading && !conversations.length"
        class="flex justify-center py-10"
      >
        <Spinner />
      </div>
      <!-- O Draggable precisa cobrir a coluna inteira: e ele quem recebe o
           drop. O aviso de coluna vazia fica atras, sem capturar o ponteiro. -->
      <div class="relative flex min-h-0 flex-1 flex-col">
        <div
          v-if="isEmpty"
          class="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 px-3 text-center"
        >
          <Icon icon="i-lucide-inbox" class="size-5 text-n-slate-8" />
          <p class="mb-0 text-xs text-n-slate-10">
            {{
              searchQuery
                ? $t('KANBAN.EMPTY_SEARCH')
                : $t('KANBAN.EMPTY_COLUMN')
            }}
          </p>
        </div>
        <Draggable
          :model-value="conversations"
          group="kanban-conversations"
          item-key="id"
          ignore="a"
          class="relative flex h-full flex-1 flex-col gap-2"
          ghost-class="opacity-40"
          drag-class="rotate-1"
          @update:model-value="emit('update:conversations', $event)"
          @change="emit('change', $event)"
        >
          <template #item="{ element }">
            <div
              data-draggable
              :class="isHidden(element) ? 'hidden' : 'cursor-grab'"
            >
              <KanbanCard
                :conversation="element"
                @open="emit('card-open', element)"
                @contextmenu="
                  emit('card-menu', { conversation: element, event: $event })
                "
              />
            </div>
          </template>
        </Draggable>
      </div>
      <Button
        v-if="hasMore"
        variant="faded"
        color="slate"
        size="sm"
        class="mt-2"
        :is-loading="loading"
        :label="$t('CHAT_LIST.LOAD_MORE_CONVERSATIONS')"
        @click="emit('load-more')"
      />
    </div>
  </section>
</template>
